import type { StripeProduct } from "@/api/types/paymentGateway";
import {
  getCreditCost,
  isActionAvailable,
  type BillableActionType,
  type CompanySize,
  type Incadrare,
  type NivelRisc,
} from "@/constants/credit";
import { findRecommendedPackage } from "./packageRecommendation";

export type JourneyStage = {
  total: number;
  label: string;
};

export type JourneyBreakdown = {
  stage1: JourneyStage;
  stage2: JourneyStage;
  stage3: JourneyStage;
  mandatoryTotal: number;
  planRemediere: JourneyStage;
  totalWithRemediere: number;
  recommendedPackageCredits: number | null;
};

export type JourneyContext = {
  size: CompanySize;
  incadrare: Incadrare;
  nivelRisc: NivelRisc;
};

const STAGE1_ACTIONS: BillableActionType[] = [
  "notificare_initiala_dnsc",
  "doc_notificare_dnsc_pdf",
  "analiza_efect_perturbator",
  "doc_analiza_efect_perturbator_pdf",
  "evidenta_decizie_inregistrare_dnsc",
];

const STAGE2_ACTIONS: BillableActionType[] = [
  "evaluare_risc_enire",
  "doc_analiza_risc_enire_pdf",
];

const STAGE3_ACTIONS: BillableActionType[] = [
  "chestionar_preliminar_v2",
  "calcul_maturitate_v2",
  "s3_raport_pdf_v2",
];

const PLAN_REMEDIERE_ACTIONS: BillableActionType[] = [
  "plan_remediere_v2",
  "doc_plan_remediere_pdf",
];

function sumActions(
  actions: readonly BillableActionType[],
  ctx: JourneyContext
): number {
  return actions.reduce((sum, action) => {
    if (!isActionAvailable(action, ctx)) return sum;
    return sum + getCreditCost(action, ctx);
  }, 0);
}

export function computeJourneyBreakdown(
  ctx: JourneyContext,
  products: readonly Pick<StripeProduct, "credits">[]
): JourneyBreakdown {
  const stage1Total = sumActions(STAGE1_ACTIONS, ctx);
  const stage2Total = sumActions(STAGE2_ACTIONS, ctx);
  const stage3Total = sumActions(STAGE3_ACTIONS, ctx);
  const planRemediereTotal = sumActions(PLAN_REMEDIERE_ACTIONS, ctx);

  const mandatoryTotal = stage1Total + stage2Total + stage3Total;
  const totalWithRemediere = mandatoryTotal + planRemediereTotal;

  return {
    stage1: {
      total: stage1Total,
      label: "Identificare & Raportare completă (incluzând generare documente)",
    },
    stage2: {
      total: stage2Total,
      label: "Analiză de Risc completă (incluzând generare documente)",
    },
    stage3: {
      total: stage3Total,
      label: "Evaluare Maturitate completă (incluzând generare documente)",
    },
    mandatoryTotal,
    planRemediere: {
      total: planRemediereTotal,
      label: "Plan de Remediere",
    },
    totalWithRemediere,
    recommendedPackageCredits: findRecommendedPackage(mandatoryTotal, products),
  };
}
